import { useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import LocalStorage from '@forwardflow/services/LocalStorage'
import { useAppStore } from '@forwardflow/store'
import { useOnboardingStoreV2 } from '@forwardflow/store/onboardingStoreV2'
import {
   CalendarConnection,
   FubConnect,
   PaymentDetails,
} from '@forwardflow/modules/onboarding'
import {
   OnboardingHandoffService,
   RedeemedHandoff,
} from '@forwardflow/services/onboardingHandoff.service'
import { SaveConflictBanner } from '@forwardflow/modules/onboarding'
import {
   HANDOFF_TITLES,
   HandoffPurpose,
   Refusal,
   describeRefusal,
} from './refusal'

/**
 * Plan 261 §C — the page a customer lands on when a conversation hands them
 * a task their browser has to finish.
 *
 * It renders the EXISTING step component for the purpose. There is no second
 * payment form, no copy of the Stripe wiring and no duplicated key
 * validation: the whole point of a handoff is that the thing which must
 * happen in a browser keeps happening where it already does.
 *
 * What this page adds is the boundary around it:
 *
 *  - the reference is redeemed ONCE, against this browser's own session, and
 *    the backend decides who the person is — the URL never does;
 *  - it checks the session itself, outside the onboarding redirect layout;
 *  - each refusal says which problem it is, because "expired", "already used"
 *    and "signed in as someone else" need three different actions from the
 *    reader and one generic error hides all of them;
 *  - it never claims the task succeeded. Finishing is reported by the backend
 *    the step already talks to, and the panel in chat re-reads it.
 */

type Status = 'signin' | 'redeeming' | 'binding' | 'ready' | 'refused'

const STEPS: Record<HandoffPurpose, () => JSX.Element> = {
   payment: () => <PaymentDetails fromAssistant />,
   'fub-connection': FubConnect,
   'calendar-connection': CalendarConnection,
}

export const OnboardingHandoffPage = () => {
   const [params] = useSearchParams()
   const navigate = useNavigate()
   const handoffId = params.get('handoff') ?? ''
   const setCurrentOrganization = useAppStore((s) => s.setCurrentOrganization)
   const initializeOnboardingData = useOnboardingStoreV2(
      (s) => s.initializeOnboardingData,
   )
   const onboardingSaveBlocked = useOnboardingStoreV2(
      (s) => s.onboardingSaveBlocked,
   )
   const subscriptionActive = useOnboardingStoreV2(
      (s) => s.onboardingData.isSubscriptionActivated === true,
   )
   const [status, setStatus] = useState<Status>('redeeming')
   const [handoff, setHandoff] = useState<RedeemedHandoff | null>(null)
   const [refusal, setRefusal] = useState<Refusal | null>(null)
   /*
    * A handoff is single-use, so redemption must happen exactly once. In
    * development React mounts effects twice, and without this the second
    * attempt consumes what the first just redeemed and the page refuses
    * itself.
    */
   const redeemed = useRef(false)

   useEffect(() => {
      /*
       * This route is deliberately OUTSIDE `ProtectedRoutesLayout` (see the
       * router), so it does its own narrow check: is there a session. The
       * layout's navigation effect would otherwise route a customer with
       * incomplete onboarding straight to `/onboarding`, defeating the
       * focused journey — and racing a single-use redemption on the way.
       */
      if (!LocalStorage?.get('access_token')) {
         setStatus('signin')
         return
      }
      if (!handoffId) {
         setStatus('refused')
         setRefusal(describeRefusal('missing', undefined))
         return
      }
      if (redeemed.current) return
      redeemed.current = true
      OnboardingHandoffService.redeem(handoffId)
         .then(async (response) => {
            const data = (response as { data?: RedeemedHandoff })?.data
            if (!data) throw new Error('empty response')
            /*
             * BIND THE VERIFIED ORGANIZATION BEFORE ANYTHING CAN BE DONE.
             *
             * The step components read global state, not this page's props:
             * the CRM step needs `onboardingData.organizationId`, and payment
             * reads the stored pricing and submits against whichever
             * organization is selected. A fresh tab has neither, and an
             * existing tab can still be holding a DIFFERENT organization — so
             * rendering the step straight after redemption could charge the
             * wrong workspace or save into it.
             *
             * The organization comes from the redeemed handoff, which the
             * server bound to the verified person. It is never taken from the
             * URL or from whatever this tab had selected.
             */
            setHandoff(data)
            setStatus('binding')
            /*
             * The RESULT decides, not whether the promise rejected.
             *
             * `initializeOnboardingData` used to swallow every failure and
             * resolve, keeping whatever record was in the store and changing
             * its organization id — so this catch never ran and the step
             * rendered against another workspace's pricing. It reports what it
             * did now, and anything short of a loaded record stops the page.
             *
             * `empty` stops it too: a payment page with no stored plan has
             * nothing to confirm, and inventing defaults to charge against is
             * worse than saying so.
             */
            await bindRef.current?.(data.organizationId)
         })
         .catch((error) => {
            const body = error?.response?.data?.data ?? error?.response?.data
            setStatus('refused')
            setRefusal(describeRefusal(body?.reason, body?.message))
         })
      // Zustand setters are stable for the lifetime of the store; listing them
      // would not change when this runs, and this must run exactly once —
      // the reference is single-use.
   }, [handoffId, initializeOnboardingData, setCurrentOrganization])

   /**
    * Load the verified workspace and decide whether the step may be shown.
    *
    * ONE function for first entry and for recovery. Recovery used to call
    * initialization and ignore its result, leaving `<Step />` mounted — so
    * payment stayed actionable while the load ran, after it failed, and after
    * an empty result installed defaults. Anything that changes the record the
    * step acts on has to go back through these states.
    */
   const bindRef = useRef<(organizationId: string) => Promise<void>>()
   async function bindWorkspace(organizationId: string) {
      setStatus('binding')
      let loaded: Awaited<ReturnType<typeof initializeOnboardingData>>
      try {
         setCurrentOrganization(organizationId)
         loaded = await initializeOnboardingData(organizationId)
      } catch {
         loaded = 'failed'
      }
      if (loaded !== 'loaded') {
         setStatus('refused')
         setRefusal(
            describeRefusal(
               'not-loaded',
               loaded === 'empty'
                  ? 'This workspace has no saved setup yet, so there is nothing for this page to act on. Start setup in your conversation first.'
                  : 'Your setup could not be loaded, so this page will not act on it.',
            ),
         )
         return
      }
      setStatus('ready')
   }
   /*
    * Held in a ref so the redemption effect can call it without listing it as
    * a dependency. That effect must run EXACTLY ONCE — the reference is
    * single-use — and a function recreated each render would re-run it.
    */
   bindRef.current = bindWorkspace

   const Step = useMemo(
      () => (handoff ? STEPS[handoff.purpose] : null),
      [handoff],
   )

   if (status === 'signin') {
      return (
         <Shell title="Sign in to continue">
            <p className="text-white/80">
               Open this link again after signing in to Forward Flow. It is
               still valid.
            </p>
            <button
               type="button"
               className="self-start rounded bg-[#9f7bff] px-4 py-2 text-sm text-white"
               onClick={() => navigate('/signin')}
            >
               Sign in
            </button>
         </Shell>
      )
   }

   if (status === 'redeeming' || status === 'binding') {
      return (
         <Shell title="Opening your task…">
            <p className="text-white/70">
               {status === 'binding'
                  ? 'Loading your workspace…'
                  : 'One moment.'}
            </p>
         </Shell>
      )
   }

   if (status === 'refused' || !handoff || !Step) {
      return (
         <Shell title="This link cannot be opened">
            <p className="text-white/80">{refusal?.message}</p>
            {refusal?.guidance ? (
               <p className="text-white/60 text-sm">{refusal.guidance}</p>
            ) : null}
            <ReturnToConversation />
         </Shell>
      )
   }

   const paymentFinished = handoff.purpose === 'payment' && subscriptionActive
   return (
      <Shell title={paymentFinished ? 'Continue your setup' : HANDOFF_TITLES[handoff.purpose]}>
         {/* Which workspace this is about, at the point of acting on it. */}
         <p className="text-white/50 text-xs">
            Workspace {handoff.organizationId}
         </p>
         {/*
           * Payment is unmounted while a save is blocked. Reloading mounts
           * it fresh, so its existing confirmation controls must be accepted
           * again against the current setup.
           *
           * Recovery reloads the RECORD, not the page: the handoff reference
           * has already been redeemed and is single-use, so a full reload
           * would refuse itself and lose the task.
           */}
         <SaveConflictBanner
            recoverLabel="Reload my setup"
            onRecover={() => {
               // Through the same states as first entry, so the step is not
               // actionable while this runs or if it does not come back
               // loaded. Reloads the RECORD, not the page: the handoff
               // reference is single-use and already redeemed.
               void bindWorkspace(handoff.organizationId)
            }}
         />
         {handoff.onboardingChangedSince ? (
            <p className="text-amber-300 text-sm">
               Your setup changed after this link was created. Check the details
               below before you confirm anything.
            </p>
         ) : null}
         {handoff.purpose === 'payment' && onboardingSaveBlocked ? (
            <p className="text-sm">
               Reload your setup and review it again before confirming payment.
            </p>
         ) : (
            <Step />
         )}
         {!paymentFinished && <ReturnToConversation />}
      </Shell>
   )
}

/**
 * No fabricated conversation URL and no dependence on the host closing this
 * tab. The reader is told what to do; the panel's own Check again is what
 * establishes whether the task actually completed.
 */
const ReturnToConversation = () => (
   <p className="text-white/60 text-sm">
      When you are finished, return to your conversation and choose{' '}
      <strong className="text-white/80">Check again</strong>. Closing this tab is
      safe.
   </p>
)

const Shell = ({
   title,
   children,
}: {
   title: string
   children: React.ReactNode
}) => (
   <div className="mx-auto flex w-full max-w-3xl flex-col gap-4 p-6">
      <h1 className="text-white text-xl font-semibold">{title}</h1>
      {children}
   </div>
)

export default OnboardingHandoffPage
